import { inr, fmtDate, fmtRel, fmtDateTime, dueTone, DUE_TEXT, fmtDue } from "@/lib/fmt";
import { LeadCard } from "@/components/crm/Kanban";
import { Empty } from "@/components/ui-helpers";
import { ATTR_FIELDS } from "@/components/crm/ViewControls";
import { ArrowUp, ArrowDown, Users } from "lucide-react";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "fbclid"];

export const rawValue = (lead, key) => {
  if (key.startsWith("utm.")) return lead.utm?.[key.slice(4)];
  if (key === "zoho_created_time") return lead.custom?.zoho_created_time || lead.created_at;
  if (UTM_KEYS.includes(key)) {
    const c = lead.custom?.[key];
    return c !== undefined && c !== null && c !== "" ? c : lead.utm?.[key];
  }
  return key in lead ? lead[key] : lead.custom?.[key];
};

export const leadValue = (lead, f, users) => {
  const v = rawValue(lead, f.key);
  if (v === undefined || v === null || v === "") return "—";
  if (f.key === "gmv") return inr(v);
  if (f.type === "owner") return users?.find((u) => u.email === v)?.name || v;
  if (f.key === "action_due") return fmtDue({ label: lead.custom?.action, due: v });
  if (f.type === "datetime" || ["created_at", "updated_at"].includes(f.key)) return fmtDateTime(v);
  if (f.type === "date") return fmtDate(v);
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
};

export function StagePill({ stage, colors, size = "sm" }) {
  const c = colors?.[stage] || "#6b7280";
  const cls = size === "lg" ? "px-3 py-1 text-sm font-semibold gap-2" : "px-2.5 py-1 text-xs font-medium gap-1.5";
  return <span className={`inline-flex items-center rounded-full border whitespace-nowrap ${cls}`} style={{ background: `${c}1f`, borderColor: `${c}55`, color: `color-mix(in srgb, ${c} 75%, var(--text))` }} data-testid="stage-pill">
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />{stage || "—"}</span>;
}

function Cell({ l, f, users, colors }) {
  if (f.key === "stage") return <StagePill stage={l.stage} colors={colors} />;
  if (f.key === "next_task") {
    const t = l.next_task;
    if (!t) return <span className="text-[var(--text-faint)]">No open task</span>;
    const tone = dueTone(t.due);
    return <span className="inline-flex items-center gap-2 max-w-[240px]" data-testid={`next-task-${l._id}`}><span className="truncate text-[var(--text)]">{t.label}</span>
      <span className={`text-xs shrink-0 ${DUE_TEXT[tone] || "text-[var(--text-faint)]"}`}>{fmtDue(t)}</span>{t.open > 1 && <span className="text-[10px] text-[var(--text-faint)] shrink-0">+{t.open - 1}</span>}</span>;
  }
  if (f.key === "owner") return l.owner ? <span>{leadValue(l, f, users)}</span> : <span className="text-[var(--text-faint)]">Unassigned</span>;
  if (f.key === "qualification" && l.qualification) return <span className={`pill ${l.qualification === "qualified" ? "pill-ok" : "pill-breach"}`}>{l.qualification}</span>;
  if (f.key === "email" && l.email) return <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-[var(--ai)]">{l.email}</a>;
  const v = leadValue(l, f, users);
  return v === "—" ? <span className="text-[var(--text-faint)]">—</span> : v;
}

export function LeadTable({ leads, fields, columns, onOpen, users, colors, sort, onSort, selected = [], onSelect, onCreate }) {
  const all = [...fields, ...ATTR_FIELDS];
  const cols = columns.map((k) => all.find((f) => f.key === k)).filter(Boolean);
  if (!leads.length) return <Empty label="No leads match this view" icon={Users} action="Add a lead" onAction={onCreate} />;
  const allOn = selected.length === leads.length;
  const toggle = (id) => onSelect(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const head = (c) => (
    <th key={c.key} onClick={() => !c.nosort && onSort(c.key)} data-testid={`col-head-${c.key}`}
      className={`text-left px-4 h-11 whitespace-nowrap cursor-pointer select-none hover:opacity-100 bg-[var(--thead-bg)] text-[var(--thead-text)] border-b border-[var(--border-strong)] ${c.key === "name" ? "sticky left-0 z-20" : ""}`}>
      <span className="inline-flex items-center gap-1">{c.label}{sort?.field === c.key && (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}</span>
    </th>
  );
  return (
    <div className="panel rounded-t-lg overflow-auto max-h-[calc(100vh-290px)]" data-testid="lead-table">
      <table className="w-full table-dense border-separate border-spacing-0">
        <thead className="sticky top-0 z-10"><tr>
          <th className="w-10 px-4 bg-[var(--thead-bg)] border-b border-[var(--border-strong)]"><input type="checkbox" checked={allOn} onChange={() => onSelect(allOn ? [] : leads.map((l) => l._id))} data-testid="select-all" /></th>
          {cols.map(head)}</tr></thead>
        <tbody>
          {leads.map((l) => {
            const tone = dueTone(l.next_task?.due);
            const late = tone === "late";
            const tint = late ? "var(--breach)" : tone === "today" ? "var(--warn)" : null;
            return (
            <tr key={l._id} onClick={() => onOpen(l._id)} className={`cursor-pointer group ${selected.includes(l._id) ? "bg-[color-mix(in_srgb,var(--ai)_12%,transparent)]" : late ? "bg-[color-mix(in_srgb,var(--breach)_8%,transparent)]" : tone === "today" ? "bg-[color-mix(in_srgb,var(--warn)_6%,transparent)]" : ""}`} data-testid={`lead-row-${l._id}`} data-overdue={late ? "1" : undefined} data-due-today={tone === "today" ? "1" : undefined}>
              <td className="px-4 border-b border-[var(--border)] group-hover:bg-[var(--hover)]" style={tint ? { boxShadow: `inset 3px 0 0 ${tint}` } : undefined} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selected.includes(l._id)} onChange={() => toggle(l._id)} data-testid={`select-${l._id}`} />
              </td>
              {cols.map((c) => (
                <td key={c.key} className={`px-4 h-[52px] whitespace-nowrap max-w-[260px] truncate border-b border-[var(--border)] group-hover:bg-[var(--hover)] ${c.key === "name" ? `font-semibold text-[var(--text)] sticky left-0 z-[1] ${selected.includes(l._id) ? "bg-[color-mix(in_srgb,var(--ai)_12%,var(--panel))]" : late ? "bg-[color-mix(in_srgb,var(--breach)_8%,var(--panel))]" : tone === "today" ? "bg-[color-mix(in_srgb,var(--warn)_6%,var(--panel))]" : "bg-[var(--panel)]"}` : "text-[var(--text-dim)]"}`}>
                  <Cell l={l} f={c} users={users} colors={colors} />
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function LeadCards({ leads, onOpen, users, colors }) {
  if (!leads.length) return <Empty label="No leads match this view" />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="lead-cards">
      {leads.map((l) => (
        <div key={l._id} className="relative">
          <LeadCard lead={l} onOpen={onOpen} users={users} extra={l.next_task && <div className={`mt-1.5 text-[11px] truncate ${DUE_TEXT[dueTone(l.next_task.due)] || "text-[var(--text-dim)]"}`} data-testid={`card-next-task-${l._id}`}>Next: {l.next_task.label} · {fmtDue(l.next_task)}</div>} />
          <span className="absolute top-2 right-9"><StagePill stage={l.stage} colors={colors} /></span>
        </div>
      ))}
    </div>
  );
}
